<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  serviceNode,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
  faqNode,
} from "../../../composables/useSeo";
import {
  useLocations,
  humanize,
  serviceContent,
  CORE_CITIES,
} from "~/composables/useLocations";
import { usePhoneNumber } from "../../../composables/usePhoneNumber";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { servicePrice, serviceLabel } = usePricing();

const route = useRoute();
const cityParam = String(route.params.city || "")
  .trim()
  .toLowerCase();
const serviceParam = String(route.params.service || "")
  .trim()
  .toLowerCase();

// Get centralized data
const { cities, services } = useLocations();

if (!cities.includes(cityParam) || !services.includes(serviceParam)) {
  throw createError({ statusCode: 404, statusMessage: "Page Not Found" });
}

const cityHuman = humanize(cityParam);
const serviceHuman = humanize(serviceParam);

const currentService =
  serviceContent[serviceParam as keyof typeof serviceContent];
const displayedServicePrice = servicePrice(serviceParam);
const displayedServiceLabel = serviceLabel(serviceParam);

// Check if this is a core city (SEO authority page)
const isCoreCity = CORE_CITIES.includes(cityParam as any);

// SEO - All city pages should be indexed
useSeoBase({
  title: `${serviceHuman} in ${cityHuman}, MN | Junk Guys MN`,
  description: `Professional ${serviceHuman.toLowerCase()} services in ${cityHuman}, Minnesota. Same-day service available.`,
  canonical: `https://junkguysmn.com/city/${cityParam}/${serviceParam}`,
  ogImage: `https://junkguysmn.com/city/${cityParam}/${serviceParam}/og-image.jpg`,
  noindex: false, // All cities should be indexed by Google
});

// Breadcrumb schema
const crumbs = [
  { name: "Home", url: "https://junkguysmn.com" },
  { name: "Cities", url: "https://junkguysmn.com/services" },
  { name: cityHuman, url: `https://junkguysmn.com/city/${cityParam}` },
  {
    name: currentService.title,
    url: `https://junkguysmn.com/city/${cityParam}/${serviceParam}`,
  },
];

// FAQ data
const faqs = [
  {
    q: `How much does ${currentService.title.toLowerCase()} cost in ${cityHuman}?`,
    a: `Our ${currentService.title.toLowerCase()} pricing in ${cityHuman} is ${displayedServicePrice.value} and varies based on volume and complexity. We provide free estimates and transparent pricing.`,
  },
  {
    q: `Do you offer same-day ${currentService.title.toLowerCase()} in ${cityHuman}?`,
    a: `Yes, we offer same-day ${currentService.title.toLowerCase()} in most ${cityHuman} areas when you call early. Contact us for availability.`,
  },
  {
    q: `What areas of ${cityHuman} do you serve?`,
    a: `We serve all neighborhoods in ${cityHuman} and surrounding areas. Our team is familiar with local regulations and disposal requirements.`,
  },
];

useJsonLd([
  orgNode(),
  localBusinessNode(),
  serviceNode(cityHuman, currentService.title),
  breadcrumbNode(crumbs),
  faqNode(faqs),
]);

// Related services for internal linking
const relatedServices = services.filter((s) => s !== serviceParam).slice(0, 6);

// Nearby cities for internal linking
const nearbyCities = cities.filter((c) => c !== cityParam).slice(0, 6);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden"
    >
      <div class="absolute inset-0 bg-black/20"></div>
      <div
        class="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-24"
      >
        <!-- Breadcrumb Navigation -->
        <nav class="mb-8 text-sm text-slate-300">
          <div class="flex items-center space-x-2">
            <NuxtLink to="/" class="hover:text-white transition-colors"
              >Home</NuxtLink
            >
            <span class="text-slate-400">/</span>
            <NuxtLink to="/services" class="hover:text-white transition-colors"
              >Services</NuxtLink
            >
            <span class="text-slate-400">/</span>
            <span class="text-white capitalize">{{ cityHuman }}, MN</span>
          </div>
        </nav>

        <!-- Main Hero Content -->
        <div class="max-w-4xl">
          <div class="flex items-center gap-4 mb-6">
            <div class="text-6xl">{{ currentService.icon }}</div>
            <div>
              <h1 class="text-4xl lg:text-6xl font-bold mb-4 capitalize">
                {{ serviceHuman }} in {{ cityHuman }}, MN
              </h1>
              <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
                {{ currentService.description }}
              </p>
            </div>
          </div>

          <!-- Quick Info Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <div
              class="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
            >
              <div class="text-2xl font-bold text-primary-light">
                {{ displayedServicePrice }}
              </div>
              <div class="text-sm text-slate-300">
                {{ displayedServiceLabel }}
              </div>
            </div>
            <div
              class="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
            >
              <div class="text-2xl font-bold text-primary-light">1-4 Hours</div>
              <div class="text-sm text-slate-300">Typical Duration</div>
            </div>
            <div
              class="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20"
            >
              <div class="text-2xl font-bold text-primary-light">Same Day</div>
              <div class="text-sm text-slate-300">Available</div>
            </div>
          </div>

          <!-- CTA Buttons -->
          <div class="flex flex-wrap gap-4 mt-8">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
            >
              <svg
                class="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                ></path>
              </svg>
              Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg border border-white/30 backdrop-blur-sm transition-all duration-200 transform hover:-translate-y-1"
            >
              <svg
                class="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                ></path>
              </svg>
              Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Main Content -->
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <!-- Main Content Column -->
        <div class="lg:col-span-2 space-y-12">
          <!-- What We Handle Section -->
          <section
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
          >
            <div class="flex items-center gap-3 mb-6">
              <div class="text-3xl">{{ currentService.icon }}</div>
              <h2 class="text-3xl font-bold text-slate-900">What We Handle</h2>
            </div>
            <p class="text-lg text-slate-600 mb-6">
              {{ currentService.description }}
            </p>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="example in currentService.examples"
                :key="example"
                class="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
              >
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">{{ example }}</span>
              </div>
            </div>
          </section>

          <!-- How It Works Section -->
          <section
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
          >
            <h2 class="text-3xl font-bold text-slate-900 mb-8">How It Works</h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="text-center">
                <div
                  class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <span class="text-2xl font-bold text-primary-main">1</span>
                </div>
                <h3 class="text-lg font-semibold text-slate-900 mb-2">
                  Fast Estimate
                </h3>
                <p class="text-slate-600">
                  Text a photo or book an on-site quote
                </p>
              </div>
              <div class="text-center">
                <div
                  class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <span class="text-2xl font-bold text-primary-main">2</span>
                </div>
                <h3 class="text-lg font-semibold text-slate-900 mb-2">
                  We Haul & Sweep
                </h3>
                <p class="text-slate-600">
                  On time, careful removal, broom-clean finish
                </p>
              </div>
              <div class="text-center">
                <div
                  class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <span class="text-2xl font-bold text-primary-main">3</span>
                </div>
                <h3 class="text-lg font-semibold text-slate-900 mb-2">
                  Responsible Disposal
                </h3>
                <p class="text-slate-600">
                  Donate/recycle when possible; receipts on request
                </p>
              </div>
            </div>
          </section>

          <!-- Pricing Section -->
          <section
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
          >
            <h2 class="text-3xl font-bold text-slate-900 mb-6">Pricing</h2>
            <div
              class="bg-gradient-to-r from-primary-main/5 to-blue-50 rounded-xl p-6 border border-primary-light"
            >
              <p class="text-lg text-slate-700 mb-4">
                We price by truck volume with on-site confirmation. Single items
                have flat rates.
              </p>
              <NuxtLink
                to="/pricing"
                class="inline-flex items-center text-primary-main hover:text-primary-dark font-semibold text-lg group"
              >
                See detailed pricing →
                <svg
                  class="w-5 h-5 ml-1 transform transition-transform group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  ></path>
                </svg>
              </NuxtLink>
            </div>
          </section>

          <!-- Service Areas Section -->
          <section
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
          >
            <h2 class="text-3xl font-bold text-slate-900 mb-6">
              Service Areas Near {{ cityHuman }}
            </h2>
            <p class="text-lg text-slate-600">
              We serve {{ cityHuman }} and surrounding neighborhoods across the
              Twin Cities metro area.
            </p>
          </section>
        </div>

        <!-- Sidebar Column -->
        <div class="space-y-6">
          <!-- Quick Contact Card -->
          <div
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sticky top-24"
          >
            <h3 class="text-xl font-bold text-slate-900 mb-4">
              Get Started Today
            </h3>
            <div class="space-y-4">
              <a
                :href="phoneNumberTel"
                @click="trackConversion"
                class="w-full bg-primary-main hover:bg-primary-dark text-white font-semibold py-3 px-4 rounded-lg text-center block transition-colors"
              >
                📞 Call Now
              </a>
              <NuxtLink
                to="/book"
                class="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-3 px-4 rounded-lg text-center block transition-colors"
              >
                📅 Book Online
              </NuxtLink>
            </div>

            <div class="mt-6 pt-6 border-t border-slate-200">
              <div class="text-sm text-slate-600">
                <div class="flex items-center gap-2 mb-2">
                  <svg
                    class="w-4 h-4 text-primary-main"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  Available: Mon-Sat 8AM-6PM
                </div>
                <div class="flex items-center gap-2 mb-2">
                  <svg
                    class="w-4 h-4 text-primary-main"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                  Licensed & Insured
                </div>
                <div class="flex items-center gap-2">
                  <svg
                    class="w-4 h-4 text-primary-main"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    ></path>
                  </svg>
                  Eco-friendly disposal
                </div>
              </div>
            </div>
          </div>

          <!-- Trust Indicators -->
          <div
            class="bg-white rounded-2xl shadow-sm border border-slate-200 p-6"
          >
            <h3 class="text-xl font-bold text-slate-900 mb-4">Why Choose Us</h3>
            <div class="space-y-3">
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-primary-main"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    ></path>
                  </svg>
                </div>
                <span class="text-sm text-slate-700">Same-day service</span>
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-primary-main"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                    ></path>
                  </svg>
                </div>
                <span class="text-sm text-slate-700">Transparent pricing</span>
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center"
                >
                  <svg
                    class="w-4 h-4 text-primary-main"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                    ></path>
                  </svg>
                </div>
                <span class="text-sm text-slate-700">Responsible disposal</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Final CTA Section -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Ready to Reclaim Your Space?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Get your {{ serviceHuman.toLowerCase() }} done today in
          {{ cityHuman }}. Fast, reliable, and eco-friendly.
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            @click="trackConversion"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200 transform hover:-translate-y-1"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- Internal Linking Blocks for Ranking Help -->
    <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
      <!-- Related Services in this City -->
      <section
        class="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mb-8"
      >
        <h2 class="text-2xl font-bold text-slate-900 mb-6">
          Related Services in {{ cityHuman }}
        </h2>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <NuxtLink
            v-for="s in [
              'appliance-removal',
              'furniture-removal',
              'garage-cleanout',
            ]"
            :key="s"
            :to="`/city/${cityParam}/${s}`"
            class="rounded-lg border border-slate-200 bg-white p-4 hover:bg-slate-50 hover:border-primary-light transition-all duration-200 capitalize text-center"
          >
            <div class="text-lg font-medium text-slate-900">
              {{ s.replace(/-/g, " ") }}
            </div>
            <div class="text-sm text-slate-600">in {{ cityHuman }}</div>
          </NuxtLink>
        </div>
      </section>

      <!-- Nearby Cities for This Service -->
      <section
        class="bg-white rounded-2xl shadow-sm border border-slate-200 p-8"
      >
        <h2 class="text-2xl font-bold text-slate-900 mb-6">Nearby Areas</h2>
        <div class="flex flex-wrap gap-3">
          <NuxtLink
            v-for="c in ['saint-paul', 'edina', 'eagan', 'bloomington']"
            :key="c"
            :to="`/city/${c}/${serviceParam}`"
            class="rounded-lg border border-slate-200 bg-white px-4 py-3 hover:bg-slate-50 hover:border-primary-light transition-all duration-200 capitalize text-center"
          >
            <div class="text-sm font-medium text-slate-900">
              {{ c.replace(/-/g, " ") }}
            </div>
            <div class="text-xs text-slate-600">
              {{ serviceHuman.toLowerCase() }}
            </div>
          </NuxtLink>
        </div>
      </section>
    </div>
  </div>
</template>
